// =============================================================================
// SIREPPAR - Renglón de afiliación del expediente (el 4‰)
// =============================================================================
// Vivía dentro de TableroDocumentacion, como el último renglón de su tabla. Se
// extrajo cuando el tablero se reemplazó por el panel de secciones: el 4‰ no es una
// fila de documentación —no hay archivo que aprobar— pero tiene que seguir a la
// vista, porque es el requisito que decide si el partido subsiste (Art. 15).
//
// Es de SOLO LECTURA. El cotejo ficha por ficha vive en el módulo de Afiliados y el
// link lleva allá.
// =============================================================================

import { Link } from 'react-router-dom';

export interface ResumenAfiliados {
  total: number;
  afiliadosValidos: number;
  minimoRequerido: number;
}

interface Props {
  partidoId?: string;
  /** `null`/`undefined` = todavía cargando. Un partido sin afiliados llega con ceros. */
  afiliados?: ResumenAfiliados | null;
}

export default function ResumenAfiliadosExpediente({ partidoId, afiliados }: Props) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-8">
      <div className="grid grid-cols-[minmax(0,17rem)_minmax(0,1fr)] gap-3 px-5 py-4 bg-primary-50 items-center">
        <div>
          <p className="text-[11px] font-bold text-primary-800 uppercase tracking-widest">Afiliación</p>
          {partidoId && (
            <Link to={`/afiliados?partido=${partidoId}`} className="text-[13px] font-medium text-primary-600 hover:text-primary-700 hover:underline">
              Padrón de afiliados →
            </Link>
          )}
        </div>
        {afiliados ? (() => {
          const cumple = afiliados.afiliadosValidos >= afiliados.minimoRequerido;
          const pct = afiliados.minimoRequerido > 0
            ? Math.min(100, (afiliados.afiliadosValidos / afiliados.minimoRequerido) * 100)
            : 100;
          const faltan = Math.max(0, afiliados.minimoRequerido - afiliados.afiliadosValidos);
          return (
            <div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className={`text-2xl font-bold leading-none ${cumple ? 'text-green-700' : 'text-red-700'}`}>
                  {afiliados.afiliadosValidos.toLocaleString('es-AR')}
                </span>
                <span className="text-lg text-gray-400 leading-none">/</span>
                <span className="text-lg font-bold text-gray-700 leading-none">
                  {afiliados.minimoRequerido.toLocaleString('es-AR')}
                </span>
                <span className="text-xs text-gray-600">válidos sobre el mínimo del 4‰</span>
                {cumple ? (
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">cumple</span>
                ) : (
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                    faltan {faltan.toLocaleString('es-AR')}
                  </span>
                )}
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-primary-100 overflow-hidden">
                <div
                  className={`h-full rounded-full ${cumple ? 'bg-green-600' : 'bg-red-600'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="text-[11px] text-gray-500 mt-1.5">
                {afiliados.total.toLocaleString('es-AR')} fichas presentadas ·{' '}
                {(afiliados.total - afiliados.afiliadosValidos).toLocaleString('es-AR')} observadas en el cotejo
              </p>
            </div>
          );
        })() : (
          <p className="text-xs text-gray-400">Cargando el resumen de afiliación…</p>
        )}
      </div>
    </div>
  );
}
